import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {NormalizedRequestResponseSample} from '../RequestConstants';
import {RequestFulfillment} from './FulfillmentConstants';
import {RequestFulfillmentAction, RequestFulfillmentActionType} from './FulfillmentReduxActions';

export interface FulfillmentsState {
  fulfillmentIdsByEntryId: {[entryId: string]: Array<string>};
  fulfillmentIdsByRequestId: {[requestId: string]: Array<string>};
  fulfillmentsById: {[fulfillmentId: string]: RequestFulfillment};
  responsesByRequestId: {[requestId: string]: {samples: Array<NormalizedRequestResponseSample>}};
}

export const FULFILLMENTS_INITIAL_STATE: FulfillmentsState = {
  fulfillmentIdsByEntryId: {},
  fulfillmentIdsByRequestId: {},
  fulfillmentsById: {},
  responsesByRequestId: {},
};

export default function reduceFulfillments(
  state: FulfillmentsState = FULFILLMENTS_INITIAL_STATE,
  action: RequestFulfillmentAction
): FulfillmentsState {
  switch (action.type) {
    case RequestFulfillmentActionType.REQUEST_RESPONSE_FETCH_COMPLETED: {
      const {requestId, samples} = action;
      return reactUpdate(state, {
        responsesByRequestId: {[requestId]: {$set: {samples}}},
      });
    }

    case RequestFulfillmentActionType.FULFILLMENTS_LOADED_FOR_ENTRY: {
      const {entryId, requestFulfillments} = action;
      const newFulfillmentIdsByRequestId = _.chain(requestFulfillments)
        .groupBy('requestId') // eslint-disable-next-line no-restricted-syntax
        .mapObject((fulfillments) => _.pluck(fulfillments, 'id'))
        .value() as unknown as {[requestId: string]: Array<string>};

      return reactUpdate(state, {
        // eslint-disable-next-line no-restricted-syntax
        fulfillmentIdsByEntryId: {[entryId]: {$set: _.pluck(requestFulfillments, 'id')}},
        fulfillmentIdsByRequestId: {
          $merge: newFulfillmentIdsByRequestId,
        },
        fulfillmentsById: {$merge: _.indexBy(requestFulfillments, 'id')},
      });
    }

    case RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCH_STARTED: {
      const {requestIds} = action;
      return reactUpdate(state, {
        fulfillmentIdsByRequestId: {
          $apply: (fulfillmentIdsByRequestId: FulfillmentsState['fulfillmentIdsByRequestId']) =>
            // eslint-disable-next-line no-restricted-syntax
            _.omit(fulfillmentIdsByRequestId, requestIds),
        },
      });
    }

    case RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCHED: {
      const {fulfillments} = action;
      return reactUpdate(state, {
        fulfillmentsById: {$merge: _.indexBy(fulfillments, 'id')},
      });
    }

    case RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCHED_FOR_REQUESTS: {
      const {requestIds, fulfillments} = action;

      const newFulfillmentIdsByRequestId = _.object(
        requestIds.map((requestId) => {
          const fulfillmentsForRequest = fulfillments.filter(
            (fulfillment) => fulfillment.requestId === requestId
          );
          // eslint-disable-next-line no-restricted-syntax
          return [requestId, _.pluck(fulfillmentsForRequest, 'id')];
        })
      );

      return reactUpdate(state, {
        fulfillmentIdsByRequestId: {$merge: newFulfillmentIdsByRequestId},
        fulfillmentsById: {$merge: _.indexBy(fulfillments, 'id')},
      });
    }

    case RequestFulfillmentActionType.REQUEST_FULFILLMENT_UPDATED: {
      const {fulfillment} = action;
      return reactUpdate(state, {
        fulfillmentsById: {[fulfillment.id]: {$set: fulfillment}},
      });
    }

    case RequestFulfillmentActionType.REQUEST_FULFILLMENTS_DELETED: {
      const {fulfillmentIds} = action;
      return reactUpdate(state, {
        fulfillmentsById: {
          $apply: (fulfillmentsById: FulfillmentsState['fulfillmentsById']) =>
            // eslint-disable-next-line no-restricted-syntax
            _.omit(fulfillmentsById, fulfillmentIds),
        },
        fulfillmentIdsByRequestId: {
          $apply: (fulfillmentIdsByRequestId: FulfillmentsState['fulfillmentIdsByRequestId']) =>
            removeIdsFromMappedObjects(fulfillmentIds, fulfillmentIdsByRequestId),
        },
        fulfillmentIdsByEntryId: {
          $apply: (fulfillmentIdsByEntryId: FulfillmentsState['fulfillmentIdsByEntryId']) =>
            removeIdsFromMappedObjects(fulfillmentIds, fulfillmentIdsByEntryId),
        },
      });
    }

    default:
      return state;
  }
}

/**
 * Removes all items in @idsToRemove from all values in the @idToListMapping object, and then deletes any K-V
 * pairs for which the value list is now empty.
 *
 * @param idsToRemove - a list of IDs to remove
 * @param idToListMapping - An object that maps a single ID to a list of IDs
 * @returns the resulting object
 */
function removeIdsFromMappedObjects<T>(
  idsToRemove: Array<string>,
  idToListMapping: {[key: string]: Array<string>}
): {[key: string]: Array<string>} {
  return _.chain(idToListMapping) // eslint-disable-next-line no-restricted-syntax
    .mapObject((ids) => _.without(ids, ...idsToRemove))
    .omit((value: Array<string>) => _.isEmpty(value))
    .value() as unknown as {[key: string]: Array<string>};
}
